const MAX_DATA_ROW = 5000;
const BLUE = '173D61';
const GOLD = 'D5AD3F';
const BORDER = 'CBD8E6';

const HEADERS = [
  'NomPartenaire', 'StatutJuridique', 'NIF', 'STAT', 'ContactPrincipal',
  'EmailPartenaire', 'TéléphonePartenaire', 'NumeroMarche', 'Composante',
  'Financement', 'ObjetContrat', 'Direction / Antenne', 'ZoneIntervention',
  'MontantMarche', 'DateDebut', 'DateFin', 'Date dernière évaluation',
  "Résultat de l'évaluation", 'Date Rupture de contrat', 'Cause rupture de Contrat',
  'situation', 'NomPersonnel', 'CIN', 'Fonction', 'emailPersonnel', 'adresse',
  'telPersonnel', 'dateNaissance', 'diplôme', 'filiere', 'SituationPersonnel',
  'DateDebutContratPersonnel', 'DateFinContratPersonnel', 'CauseRupturePersonnel',
];

const COLUMN_INSTRUCTIONS = {
  NomPartenaire: 'Nom officiel de l’association, de l’entreprise ou du consultant.',
  StatutJuridique: 'Exemple : ASSOCIATION, ONG, Consultant individuel ou Entreprise.',
  NIF: 'Numéro d’identification fiscale du partenaire.',
  STAT: 'Référence STAT du partenaire.',
  ContactPrincipal: 'Nom complet du représentant principal.',
  EmailPartenaire: 'Adresse e-mail du partenaire.',
  TéléphonePartenaire: 'Numéro de téléphone du partenaire, au format local ou international.',
  NumeroMarche: 'Code officiel du contrat, par exemple C1a124G060.',
  Composante: 'Composante du contrat, par exemple TMNC, TMDH, FSP, MACC_FSP ou INFRA-POST CATA.',
  Financement: 'Financement du contrat, par exemple FSR, UNICEF ou LEAD.',
  ObjetContrat: 'Résumé court de l’objet du contrat, par exemple « Ciblage des bénéficiaires TMNC ».',
  'Direction / Antenne': 'Direction ou antenne FID responsable du contrat.',
  ZoneIntervention: 'Zone ou district couvert par le contrat.',
  MontantMarche: 'Montant total du marché en ariary, par exemple 100000000.',
  DateDebut: 'Date de début du contrat au format jj/mm/aaaa.',
  DateFin: 'Date de fin du contrat au format jj/mm/aaaa.',
  'Date dernière évaluation': 'Date de la dernière évaluation au format jj/mm/aaaa.',
  "Résultat de l'évaluation": 'Résultat suivant les critères d’évaluation.',
  'Date Rupture de contrat': 'Date de rupture du contrat entre le prestataire et le FID, au format jj/mm/aaaa.',
  'Cause rupture de Contrat': 'Cause de rupture du contrat entre le prestataire et le FID.',
  situation: 'Situation du contrat : EN COURS, ACHEVÉ ou RÉSILIÉ.',
  NomPersonnel: 'Nom complet du membre du personnel affecté au contrat.',
  CIN: 'Numéro de CIN du personnel, sans espace ; saisir en tant que nombre.',
  Fonction: 'Rôle du personnel, par exemple Chef de mission, Superviseur ou Socio-organisateur.',
  emailPersonnel: 'Adresse e-mail du membre du personnel.',
  adresse: 'Adresse de la résidence réelle du membre du personnel.',
  telPersonnel: 'Numéro de téléphone du membre du personnel.',
  dateNaissance: 'Date de naissance du personnel au format jj/mm/aaaa.',
  diplôme: 'Diplôme principal le plus élevé du personnel.',
  filiere: 'Filière du personnel, par exemple Gestion ou Droit.',
  SituationPersonnel: 'Situation du personnel sur ce contrat, par exemple EN COURS ou RÉSILIÉ.',
  DateDebutContratPersonnel: 'Date de début du contrat du personnel au format jj/mm/aaaa.',
  DateFinContratPersonnel: 'Date de fin du contrat du personnel au format jj/mm/aaaa.',
  CauseRupturePersonnel: 'Cause de rupture du contrat du personnel, si applicable.',
};

const headerStyle = (row) => {
  row.height = 36;
  row.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${BLUE}` } };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = { bottom: { style: 'thin', color: { argb: `FF${BORDER}` } } };
  });
};

const addListValidation = (sheet, range, formula, allowBlank, prompt) => {
  sheet.dataValidations.add(range, {
    type: 'list',
    allowBlank,
    formulae: [formula],
    showErrorMessage: true,
    errorStyle: 'error',
    errorTitle: 'Valeur invalide',
    error: 'Choisissez une valeur dans la liste proposée.',
    showInputMessage: true,
    promptTitle: 'Référence PGI / SPM',
    prompt,
  });
};

export async function buildPartnerImportTemplate(payload) {
  const partners = payload?.partners;
  if (!Array.isArray(partners)) {
    throw new Error('Liste des partenaires invalide pour le modèle.');
  }

  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'PGI FID';
  workbook.title = 'Mise à jour des données partenaire';

  const instructions = workbook.addWorksheet('Instructions', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });
  const introduction = [
    ['Mise à jour des données partenaire', ''],
    ['1. Télécharger', 'Téléchargez un nouveau modèle avant chaque campagne. Les références disponibles proviennent de PGI et, si accessible, du suivi marché.'],
    ['2. Choisir', 'Dans le canevas, choisissez le partenaire, puis son contrat dans les listes proposées.'],
    ['3. Renseigner', 'Remplissez les informations partenaire, marché, évaluation et personnel à mettre à jour.'],
    ['Correction', 'Si une anomalie apparaît, corrigez le fichier puis utilisez « Remplacer le fichier ».'],
    ['Personnel', 'NomPersonnel est obligatoire dès qu’une information personnel est renseignée.'],
    ['Contrôle', 'Les listes Excel facilitent la saisie. Le serveur vérifie à nouveau les références lors de l’import.'],
    ['Saisie', 'Toutes les saisies doivent être en lettres capitales, sauf les adresses e-mail. Ne faites pas précéder les noms de « Monsieur » ou « Madame ».'],
  ];
  introduction.forEach((values) => instructions.addRow(values));
  headerStyle(instructions.getRow(1));
  instructions.getColumn(1).width = 32;
  instructions.getColumn(2).width = 105;
  instructions.getColumn(2).alignment = { wrapText: true, vertical: 'top' };
  for (let row = 2; row <= introduction.length; row += 1) {
    instructions.getRow(row).height = row === introduction.length ? 44 : 34;
    instructions.getCell(`A${row}`).font = { bold: true, color: { argb: `FF${BLUE}` } };
  }

  instructions.addRow([]);
  const columnHeader = instructions.addRow(['Colonne', 'Données à saisir']);
  headerStyle(columnHeader);
  HEADERS.forEach((header) => {
    const row = instructions.addRow([header, COLUMN_INSTRUCTIONS[header]]);
    row.height = 34;
    row.getCell(1).font = { bold: true, color: { argb: `FF${BLUE}` } };
    row.getCell(1).alignment = { vertical: 'middle', wrapText: true };
    row.getCell(2).alignment = { vertical: 'middle', wrapText: true };
    row.eachCell((cell) => {
      cell.border = { bottom: { style: 'hair', color: { argb: `FF${BORDER}` } } };
    });
  });

  const canevas = workbook.addWorksheet('canevas', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });
  canevas.addRow(HEADERS);
  headerStyle(canevas.getRow(1));
  canevas.autoFilter = `A1:AH1`;
  HEADERS.forEach((_, index) => {
    canevas.getColumn(index + 1).width = index === 0 || index === 10 ? 32 : 21;
  });
  canevas.getColumn(8).width = 29;
  canevas.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${GOLD}` } };
  canevas.getCell('H1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${GOLD}` } };
  canevas.getCell('A1').font = { bold: true, color: { argb: `FF${BLUE}` } };
  canevas.getCell('H1').font = { bold: true, color: { argb: `FF${BLUE}` } };

  const references = workbook.addWorksheet('Références');
  references.addRow([
    'NomPartenaire', 'Source', 'partenaire_id', 'id_marche_spm', 'NIF', 'STAT',
    'PlageContrats', '', '', 'NumeroMarche', 'Source contrat', 'execution_marche_id', 'id_marche_spm',
  ]);
  headerStyle(references.getRow(1));
  references.getColumn(1).width = 42;
  references.getColumn(2).width = 30;
  references.getColumn(7).width = 24;
  references.getColumn(10).width = 32;
  references.views = [{ state: 'frozen', ySplit: 1 }];

  let contractRow = 3;
  partners.forEach((partner, index) => {
    const row = index + 2;
    const contracts = Array.isArray(partner.contracts) ? partner.contracts : [];
    const rangeName = partner.contract_range;
    if (!/^Contrats_P\d{6}$/.test(rangeName)) {
      throw new Error('Référence de contrat invalide reçue du serveur.');
    }
    references.getCell(`A${row}`).value = partner.name;
    references.getCell(`B${row}`).value = partner.source;
    references.getCell(`C${row}`).value = (partner.partenaire_ids || []).join(', ');
    references.getCell(`D${row}`).value = (partner.id_marche_spm || []).join(', ');
    references.getCell(`E${row}`).value = partner.nif || '';
    references.getCell(`F${row}`).value = partner.stat || '';
    references.getCell(`G${row}`).value = rangeName;

    const firstRow = contractRow;
    contracts.forEach((contract) => {
      references.getCell(`J${contractRow}`).value = contract.reference;
      references.getCell(`K${contractRow}`).value = contract.source;
      references.getCell(`L${contractRow}`).value = contract.execution_marche_id;
      references.getCell(`M${contractRow}`).value = contract.id_marche_spm;
      contractRow += 1;
    });
    workbook.definedNames.add(
      contracts.length ? `Références!$J$${firstRow}:$J$${contractRow - 1}` : 'Références!$O$2',
      rangeName,
    );
  });

  const lastPartnerRow = Math.max(partners.length + 1, 2);
  workbook.definedNames.add(`Références!$A$2:$A$${lastPartnerRow}`, 'ListePartenaires');
  workbook.definedNames.add(`Références!$A$2:$G$${lastPartnerRow}`, 'TablePartenaires');
  workbook.definedNames.add('Références!$O$2', 'Contrats_Vide');
  addListValidation(canevas, `A2:A${MAX_DATA_ROW}`, 'ListePartenaires', false,
    'Choisissez un partenaire de la liste à jour.');
  addListValidation(canevas, `H2:H${MAX_DATA_ROW}`,
    'INDIRECT(IFERROR(VLOOKUP($A2,TablePartenaires,7,FALSE),"Contrats_Vide"))',
    true, 'Choisissez d’abord le partenaire, puis l’un de ses contrats.');

  await references.protect('PGI-PARTNER-REF', {
    selectLockedCells: false,
    selectUnlockedCells: false,
    spinCount: 1000,
  });
  references.state = 'veryHidden';
  workbook.activeTab = 1;
  return workbook.xlsx.writeBuffer();
}
